/**
 * App Component
 * 
 * Main application component with routing support for payment flow.
 * Handles both the landing page and payment result pages.
 */

import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import FeaturedCourses from './components/FeaturedCourses';
import Benefits from './components/Benefits';
import VirtualClassroom from './components/VirtualClassroom';
import Testimonials from './components/Testimonials';
import CallToAction from './components/CallToAction';
import About from './components/About';
import Footer from './components/Footer';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import PaymentResult from './components/PaymentResult';
import TestModeBanner from './components/TestModeBanner';
import Profesor from './pages/Profesor';
import Nosotros from './pages/Nosotros';
import CursoGuardiaSeguridadPrivada from './pages/CursoGuardiaSeguridadPrivada';
import CursoSupervisor from './pages/CursoSupervisor';
import CatalogoCursos from './pages/CatalogoCursos';
import CursoPerfeccionamientoGuardia from './pages/CursoPerfeccionamientoGuardia';
import CursoEspecializacionGuardia from './pages/CursoEspecializacionGuardia';
import CursoElectricidadBasica from './pages/CursoElectricidadBasica';
import AdminIntereses from './pages/AdminIntereses';
import PresentialPayment from './pages/PresentialPayment';
import PresentialPaymentResult from './pages/PresentialPaymentResult';
import StandalonePayment from './pages/StandalonePayment';
import StandalonePaymentResult from './pages/StandalonePaymentResult';

/**
 * Landing Page Component
 * Main landing page with all sections
 */
function LandingPage() {
  return (
    <>
      <Header />
      <Hero />
      <FeaturedCourses />
      <Benefits />
      <VirtualClassroom />
      <Testimonials />
      <CallToAction />
      <About />
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}

/**
 * Payment Result Page Component
 * Wrapper for PaymentResult with navigation
 */
function PaymentResultPage() {
  const navigate = useNavigate();

  const handleRetry = () => {
    navigate('/cursos');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <>
      <Header />
      <PaymentResult
        onRetry={handleRetry}
        onGoHome={handleGoHome}
      />
      <Footer />
    </>
  );
}

/**
 * Main App Component
 */
function App() {
  return (
    <BrowserRouter>
      <div className="min-h-screen bg-background font-sans text-foreground overflow-x-hidden">
        {/* Test Mode Banner - shown on all pages when in integration mode */}
        <TestModeBanner />

        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/* Payment Result Page */}
          <Route path="/payment/result" element={<PaymentResultPage />} />

          {/* Teacher Panel - Requirements: 7.1, 8.1 */}
          <Route path="/profesor" element={<Profesor />} />

          {/* About Us Page */}
          <Route path="/nosotros" element={<Nosotros />} />

          {/* Courses Catalog */}
          <Route path="/cursos" element={<CatalogoCursos />} />

          {/* Course Detail Pages */}
          <Route path="/curso/guardia-os10" element={<CursoGuardiaSeguridadPrivada />} />
          <Route path="/curso/guardia-seguridad-privada" element={<CursoGuardiaSeguridadPrivada />} />
          <Route path="/curso/supervisor-seguridad" element={<CursoSupervisor />} />
          <Route path="/curso/perfeccionamiento-guardia" element={<CursoPerfeccionamientoGuardia />} />
          <Route path="/curso/especializacion-guardia" element={<CursoEspecializacionGuardia />} />
          <Route path="/curso/electricidad-basica" element={<CursoElectricidadBasica />} />

          {/* Curso descontinuado: se redirige al catálogo para no romper enlaces existentes */}
          <Route path="/curso/contabilidad" element={<Navigate to="/cursos" replace />} />

          {/* Admin Interest Management - Requirements: 8.1, 8.2, 8.3 */}
          <Route path="/admin/intereses" element={<AdminIntereses />} />

          {/* Presential Payment Pages - Requirements: 14.3, 14.4, 14.8, 14.10 */}
          <Route path="/pago-presencial/:token" element={<PresentialPayment />} />
          <Route path="/pago-presencial/resultado" element={<PresentialPaymentResult />} />

          {/* Standalone Payment Link Pages */}
          <Route path="/pago/link/:token" element={<StandalonePayment />} />
          <Route path="/pago/link/resultado" element={<StandalonePaymentResult />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
